import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'caregrid_access_token';

export const setAuthToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};
export const getAuthToken = () => localStorage.getItem(TOKEN_KEY);

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor — attach bearer token as a fallback to cookies.
// On deployed sites, edge/proxy quirks can prevent httpOnly cookies from
// being sent on subsequent requests. The Authorization header works regardless.
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — pass errors through.
// AuthContext + ProtectedRoute handle true auth expiry via /auth/me checks.
api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Users API
export const usersAPI = {
  getAll: (role) => api.get('/users', { params: { role } }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users/create', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  resetPassword: (id, new_password) => api.post(`/users/${id}/reset-password`, { new_password })
};

// Clients API
export const clientsAPI = {
  getAll: (status) => api.get('/clients', { params: { status } }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data, asLead) => api.post('/clients', data, { params: asLead ? { as_lead: true } : {} }),
  convertToActive: (id) => api.post(`/clients/${id}/convert-to-active`),
  update: (id, data) => api.put(`/clients/${id}`, data),
  discharge: (id, data) => api.post(`/clients/${id}/discharge`, data),
  readmit: (id) => api.post(`/clients/${id}/readmit`),
  getWeeklySchedule: (id) => api.get(`/clients/${id}/weekly-schedule`),
  getCaregiverDistances: (id) => api.get(`/clients/${id}/caregiver-distances`),
  updateWeeklySchedule: (id, slots, start_date, end_date) => api.put(`/clients/${id}/weekly-schedule`, { slots, start_date, end_date }),
  generateVisits: (id, weeks) => api.post(`/clients/${id}/generate-visits?weeks=${weeks || 4}`)
};

// Care Plans API
export const carePlansAPI = {
  getAll: (clientId) => api.get('/care-plans', { params: { client_id: clientId } }),
  create: (data) => api.post('/care-plans', data),
  update: (id, data) => api.put(`/care-plans/${id}`, data)
};

// Visits API
export const visitsAPI = {
  getAll: (params) => api.get('/visits', { params }),
  create: (data) => api.post('/visits', data),
  update: (id, data) => api.put(`/visits/${id}`, data),
  delete: (id) => api.delete(`/visits/${id}`),
  checkIn: (id, gps) => api.post(`/visits/${id}/check-in`, gps || {}),
  checkOut: (id, completion) => api.post(`/visits/${id}/check-out`, completion),
  supervisorCheckOut: (id, data) => api.post(`/visits/${id}/supervisor-check-out`, data),
  updateStatus: (id, status) => api.put(`/visits/${id}/status?status=${status}`),
  updateTimes: (id, data) => api.put(`/visits/${id}/times`, data),
  broadcast: (id, radius_miles) => api.post(`/visits/${id}/broadcast`, { radius_miles }),
  requestShift: (id, note) => api.post(`/visits/${id}/request-shift`, { note }),
  approveRequest: (id, caregiver_user_id) => api.post(`/visits/${id}/approve-request`, { caregiver_user_id })
};

// Open Shifts API
export const openShiftsAPI = {
  listForOffice: () => api.get('/open-shifts'),
  listForMe: () => api.get('/open-shifts/me')
};

// Visit Logs API
export const visitLogsAPI = {
  getAll: (params) => api.get('/visit-logs', { params }),
  create: (data) => api.post('/visit-logs', data)
};

// Assignments API
export const assignmentsAPI = {
  getAll: (params) => api.get('/assignments', { params }),
  create: (data) => api.post('/assignments', data),
  delete: (id) => api.delete(`/assignments/${id}`)
};

// Audit Logs API
export const auditLogsAPI = {
  getAll: (params) => api.get('/audit-logs', { params })
};

// AI API
export const aiAPI = {
  suggestCarePlan: (clientId, context) => api.post('/ai/suggest-care-plan', { client_id: clientId, context }),
  assistDocumentation: (visitId, notes) => api.post(`/ai/assist-documentation?visit_id=${visitId}&notes=${encodeURIComponent(notes)}`)
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats')
};

// Offices API
export const officesAPI = {
  getAll: () => api.get('/offices'),
  getById: (id) => api.get(`/offices/${id}`),
  create: (data) => api.post('/offices', data),
  update: (id, data) => api.put(`/offices/${id}`, data),
  delete: (id) => api.delete(`/offices/${id}`),
  switch: (id) => api.post(`/offices/${id}/switch`),
  mergeInto: (sourceId, targetId) => api.post(`/offices/${sourceId}/merge-into/${targetId}`),
  getStats: () => api.get('/offices-stats')
};

// Companies API
export const companiesAPI = {
  getAll: () => api.get('/companies'),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`)
};

// Role Permissions API
export const rolePermissionsAPI = {
  get: (officeId) => api.get('/role-permissions', { params: officeId ? { office_id: officeId } : {} }),
  update: (role, permissions, officeId) => api.put('/role-permissions', { role, permissions }, {
    params: officeId ? { office_id: officeId } : {}
  })
};

// Billing API
export const billingAPI = {
  getClientBilling: (clientId, params) => api.get(`/billing/client/${clientId}`, { params }),
  getAgencyBilling: (params) => api.get('/billing/agency', { params }),
  updateRates: (clientId, data) => api.put(`/clients/${clientId}/billing-rates`, data)
};

// Invoices API
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  updateStatus: (id, status) => api.put(`/invoices/${id}/status`, { status }),
  delete: (id) => api.delete(`/invoices/${id}`)
};

// Geofence API
export const geofenceAPI = {
  getAlerts: (params) => api.get('/geofence-alerts', { params }),
  resolveAlert: (id) => api.put(`/geofence-alerts/${id}/resolve`),
  updateClientGeofence: (clientId, radius) => api.put(`/clients/${clientId}/geofence?radius=${radius}`)
};

// Payroll API
export const payrollAPI = {
  getCaregiverPayroll: (profileId, params) => api.get(`/payroll/caregiver/${profileId}`, { params }),
  getAgencyPayroll: (params) => api.get('/payroll/agency', { params })
};

// Files API
export const filesAPI = {
  upload: (clientId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/files/upload?client_id=${clientId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  list: (clientId) => api.get('/files', { params: { client_id: clientId } }),
  download: (fileId) => api.get(`/files/${fileId}/download`, { responseType: 'blob' }),
  delete: (fileId) => api.delete(`/files/${fileId}`)
};

// Client Documents API
export const clientDocumentsAPI = {
  list: (clientId) => api.get('/client-documents', { params: { client_id: clientId } }),
  getById: (docId) => api.get(`/client-documents/${docId}`),
  create: (data) => api.post('/client-documents', data),
  update: (docId, data) => api.put(`/client-documents/${docId}`, data),
  delete: (docId) => api.delete(`/client-documents/${docId}`)
};

// Incident Reports API
export const incidentReportsAPI = {
  list: (clientId) => api.get('/incident-reports', { params: { client_id: clientId } }),
  getById: (id) => api.get(`/incident-reports/${id}`),
  create: (data) => api.post('/incident-reports', data),
  update: (id, data) => api.put(`/incident-reports/${id}`, data),
  delete: (id) => api.delete(`/incident-reports/${id}`)
};

// Messaging API
export const messagingAPI = {
  getContacts: () => api.get('/messaging/contacts'),
  getGroups: () => api.get('/messaging/groups'),
  createGroup: (data) => api.post('/messaging/groups', data),
  getConversations: () => api.get('/messaging/conversations'),
  createConversation: (data) => api.post('/messaging/conversations', data),
  getMessages: (conversationId) => api.get(`/messaging/conversations/${conversationId}/messages`),
  sendMessage: (data) => api.post('/messaging/messages', data),
  sendToGroup: (groupId, content) => api.post(`/messaging/send-to-group?group_id=${groupId}&content=${encodeURIComponent(content)}`),
  getUnreadCount: () => api.get('/messaging/unread-count')
};

// Caregiver Profiles API
export const caregiverProfilesAPI = {
  getAll: () => api.get('/caregiver-profiles'),
  getById: (id) => api.get(`/caregiver-profiles/${id}`),
  create: (data) => api.post('/caregiver-profiles', data),
  update: (id, data) => api.put(`/caregiver-profiles/${id}`, data),
  updatePayRates: (id, data) => api.put(`/caregiver-profiles/${id}/pay-rates`, data),
  deactivate: (id, data) => api.post(`/caregiver-profiles/${id}/deactivate`, data || {}),
  reactivate: (id) => api.post(`/caregiver-profiles/${id}/reactivate`),
  delete: (id) => api.delete(`/caregiver-profiles/${id}`),
  createLogin: (id, data) => api.post(`/caregiver-profiles/${id}/create-login`, data),
  linkCheck: (id) => api.get(`/caregiver-profiles/${id}/link-check`),
  relinkShifts: (id, data) => api.post(`/caregiver-profiles/${id}/relink-shifts`, data)
};

// Caregiver Files API
export const caregiverFilesAPI = {
  upload: (caregiverId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/caregiver-files/upload?caregiver_id=${caregiverId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  list: (caregiverId) => api.get('/caregiver-files', { params: { caregiver_id: caregiverId } }),
  download: (fileId) => api.get(`/caregiver-files/${fileId}/download`, { responseType: 'blob' }),
  delete: (fileId) => api.delete(`/caregiver-files/${fileId}`)
};

// Caregiver Documents API
export const caregiverDocumentsAPI = {
  list: (caregiverId) => api.get('/caregiver-documents', { params: { caregiver_id: caregiverId } }),
  create: (data) => api.post('/caregiver-documents', data),
  update: (docId, data) => api.put(`/caregiver-documents/${docId}`, data),
  delete: (docId) => api.delete(`/caregiver-documents/${docId}`)
};

// Form Templates API
export const formTemplatesAPI = {
  getAll: (appliesTo) => api.get('/form-templates', { params: { applies_to: appliesTo } }),
  getById: (id) => api.get(`/form-templates/${id}`),
  create: (data) => api.post('/form-templates', data),
  update: (id, data) => api.put(`/form-templates/${id}`, data),
  delete: (id) => api.delete(`/form-templates/${id}`),
  seedServiceAgreement: () => api.post('/form-templates/seed-service-agreement'),
};

// Form Submissions API
export const formSubmissionsAPI = {
  getAll: (params) => api.get('/form-submissions', { params }),
  getById: (id) => api.get(`/form-submissions/${id}`),
  create: (data) => api.post('/form-submissions', data),
  delete: (id) => api.delete(`/form-submissions/${id}`)
};

// Caregiver Onboarding API
export const onboardingAPI = {
  listTemplates: () => api.get('/onboarding/templates'),
  getTemplate: (id) => api.get(`/onboarding/templates/${id}`),
  uploadTemplate: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/onboarding/templates', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  templatePdf: (id) => api.get(`/onboarding/templates/${id}/pdf`, { responseType: 'blob' }),
  updateTemplate: (id, data) => api.put(`/onboarding/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/onboarding/templates/${id}`),
  listApplicants: () => api.get('/onboarding/applicants'),
  getApplicant: (id) => api.get(`/onboarding/applicants/${id}`),
  createApplicant: (data) => api.post('/onboarding/applicants', data),
  updateApplicant: (id, data) => api.put(`/onboarding/applicants/${id}`, data),
  deleteApplicant: (id) => api.delete(`/onboarding/applicants/${id}`),
  sendEmail: (id, data) => api.post(`/onboarding/applicants/${id}/send-email`, data || {}),
  signedPdf: (id, templateId) => api.get(`/onboarding/applicants/${id}/forms/${templateId}/pdf`, { responseType: 'blob' }),
  hire: (id, data) => api.post(`/onboarding/applicants/${id}/hire`, data || {}),
  applicantDocument: (id, docTypeId) => api.get(`/onboarding/applicants/${id}/documents/${docTypeId}`, { responseType: 'blob' }),
  listDocTypes: () => api.get('/onboarding/document-types'),
  createDocType: (data) => api.post('/onboarding/document-types', data),
  updateDocType: (id, data) => api.put(`/onboarding/document-types/${id}`, data),
  deleteDocType: (id) => api.delete(`/onboarding/document-types/${id}`),
  alerts: () => api.get('/onboarding/alerts'),
  dismissAlert: (id) => api.post(`/onboarding/applicants/${id}/dismiss-alert`),
  // public (no auth)
  publicPacket: (token) => axios.get(`${API_URL}/api/onboarding/public/${token}`),
  publicPdfUrl: (token, templateId) => `${API_URL}/api/onboarding/public/${token}/templates/${templateId}/pdf`,
  publicSubmit: (token, templateId, values) => axios.post(`${API_URL}/api/onboarding/public/${token}/templates/${templateId}/submit`, { values }),
  publicDocumentUrl: (token, docTypeId) => `${API_URL}/api/onboarding/public/${token}/documents/${docTypeId}`,
  publicUploadDocument: (token, docTypeId, file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API_URL}/api/onboarding/public/${token}/documents/${docTypeId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
    });
  },
};

// Weekly Billing & Payroll cycles
export const billingCyclesAPI = {
  settings: () => api.get('/billing-cycles/settings'),
  updateSettings: (data) => api.put('/billing-cycles/settings', data),
  generate: (period_start, kind) => api.post('/billing-cycles/generate', { period_start, kind }),
  runs: (kind) => api.get('/billing-cycles/runs', { params: { kind } }),
  run: (kind, periodStart) => api.get(`/billing-cycles/runs/${kind}/${periodStart}`),
  item: (id) => api.get(`/billing-cycles/items/${id}`),
  approve: (id) => api.post(`/billing-cycles/items/${id}/approve`),
  reopen: (id) => api.post(`/billing-cycles/items/${id}/reopen`),
  remove: (id) => api.delete(`/billing-cycles/items/${id}`),
  approveAll: (kind, periodStart) => api.post(`/billing-cycles/runs/${kind}/${periodStart}/approve-all`),
  pendingSummary: () => api.get('/billing-cycles/pending-summary'),
};

// Invoices API extras (email)
export const invoiceEmailAPI = {
  send: (invoiceId, data) => api.post(`/invoices/${invoiceId}/email`, data || {}),
};

// Client welcome email
export const clientEmailAPI = {
  sendWelcome: (clientId, data) => api.post(`/clients/${clientId}/welcome-email`, data || {}),
};

// Reports API
export const reportsAPI = {
  grossMargin: (params) => api.get('/reports/gross-margin', { params }),
  billingPayrollCrossCheck: (params) => api.get('/reports/billing-payroll-crosscheck', { params }),
};

// Sales Calls API
export const salesAPI = {
  getAll: (params) => api.get('/sales-calls', { params }),
  create: (data) => api.post('/sales-calls', data),
  update: (id, data) => api.put(`/sales-calls/${id}`, data),
  delete: (id) => api.delete(`/sales-calls/${id}`),
};

// QuickBooks Online API
export const quickbooksAPI = {
  status: (officeId) => api.get('/quickbooks/status', { params: officeId ? { office_id: officeId } : {} }),
  connect: (officeId) => api.get('/quickbooks/connect', { params: officeId ? { office_id: officeId } : {} }),
  disconnect: (officeId) => api.post('/quickbooks/disconnect', null, { params: officeId ? { office_id: officeId } : {} }),
  pushInvoice: (invoiceId) => api.post(`/quickbooks/invoices/${invoiceId}/push`),
};

// Office Holidays API
export const holidaysAPI = {
  get: (officeId) => api.get(`/offices/${officeId}/holidays`),
  update: (officeId, data) => api.put(`/offices/${officeId}/holidays`, data),
  add: (officeId, item) => api.post(`/offices/${officeId}/holidays`, item),
  remove: (officeId, date) => api.delete(`/offices/${officeId}/holidays/${date}`),
};

// Office Invoice Branding API
export const brandingAPI = {
  get: (officeId) => api.get(`/offices/${officeId}/branding`),
  update: (officeId, data) => api.put(`/offices/${officeId}/branding`, data),
  uploadLogo: (officeId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/offices/${officeId}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  removeLogo: (officeId) => api.delete(`/offices/${officeId}/logo`),
};

export default api;
